<script lang="ts">
  import { goto, yyyy, zzzz } from "../store.svelte";
  import Fa from "svelte-fa";
  import { faUpload, faCheck } from "@fortawesome/free-solid-svg-icons";

  const url = yyyy.uri.api;

  // Form state
  let lw_name = $state({ value: "", loading: false, success: false, danger: false, message: "" });
  let lw_srid = $state({ value: "", loading: false, success: false, danger: false, message: "" });
  let lw_tolr = $state({ value: "", loading: false, success: false, danger: false, warning: false, message: "" });
  let lw_desc = $state({ value: "" });

  // Step: 'details' or 'upload'
  let step = $state<"details" | "upload">("details");
  let creatingLivewire = $state(false);

  // Upload state
  let file_upload: HTMLInputElement;
  let uploadProgress = $state(0);
  let filename = $state("");
  let uploading = $state(false);
  let serverPulling = $state(false);
  let dragOver = $state(false);

  const nameCheck = async () => {
    if (!lw_name.value) return;
    lw_name.loading = true;

    const response = await fetch(`${url}/rpc/create_livewire_name_check`, {
      method: "post",
      body: JSON.stringify({ potential_name: lw_name.value }),
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + yyyy.sesh.logtoken.token,
      },
    });

    if (response.ok) {
      lw_name.success = true;
      lw_name.danger = false;
    } else {
      lw_name.success = false;
      lw_name.danger = true;
      const data = await response.json();
      lw_name.message = data.hint;
    }
    lw_name.loading = false;
  };

  const sridCheck = async () => {
    if (!lw_srid.value) return;
    lw_srid.loading = true;

    const response = await fetch(`${url}/rpc/create_livewire_srid_check`, {
      method: "post",
      body: JSON.stringify({ potential_srid: lw_srid.value }),
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + yyyy.sesh.logtoken.token,
      },
    });

    if (response.ok) {
      lw_srid.success = true;
      lw_srid.danger = false;
    } else {
      lw_srid.success = false;
      lw_srid.danger = true;
      const data = await response.json();
      lw_srid.message = data.hint;
    }
    lw_srid.loading = false;
  };

  const tolrCheck = () => {
    if (!lw_tolr.value) return;
    const val = parseFloat(lw_tolr.value);

    if (isNaN(val)) {
      lw_tolr.success = false;
      lw_tolr.danger = true;
      lw_tolr.warning = false;
      lw_tolr.message = "Supply a numeric value.";
    } else if (val < 0) {
      lw_tolr.success = false;
      lw_tolr.danger = true;
      lw_tolr.warning = false;
      lw_tolr.message = "Tolerance cannot be negative";
    } else if (val < 0.2) {
      lw_tolr.success = true;
      lw_tolr.danger = false;
      lw_tolr.warning = false;
      lw_tolr.message = "";
    } else {
      lw_tolr.success = false;
      lw_tolr.danger = false;
      lw_tolr.warning = true;
      lw_tolr.message = "The supplied tolerance is quite large; your mileage may vary";
    }
  };

  const canProceed = $derived(
    lw_name.success && lw_srid.success && (lw_tolr.success || lw_tolr.warning)
  );

  const createAndShowUpload = async () => {
    creatingLivewire = true;

    if (!lw_desc.value) {
      lw_desc.value = `This is ${lw_name.value} with an SRID of ${lw_srid.value} and a tolerance of ${lw_tolr.value}`;
    }

    const response = await fetch(`${url}/rpc/create_livewire`, {
      method: "post",
      body: JSON.stringify({
        lw_name: lw_name.value,
        lw_srid: lw_srid.value,
        lw_tolr: lw_tolr.value,
        lw_desc: lw_desc.value,
      }),
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + yyyy.sesh.logtoken.token,
      },
    });

    if (response.ok) {
      const data = await response.json();
      yyyy.createNewLiveWire.data = data.info;
      yyyy.createNewLiveWire.livewire = lw_name.value;
      step = "upload";
    } else {
      const data = await response.json();
      console.error(data);
      alert("Failed to create livewire");
    }

    creatingLivewire = false;
  };

  const getSignedRequest = async (file: File) => {
    const response = await fetch(`${url}/rpc/create_livewire_get_url_for_upload`, {
      method: "post",
      body: JSON.stringify({ filename: file.name, filetype: file.type }),
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + yyyy.sesh.logtoken.token,
      },
    });

    if (response.ok) {
      const data = await response.json();
      uploadFile(file, data);
    } else {
      alert("Failed to get upload URL");
    }
  };

  const fileKickOff = () => {
    if (!file_upload.files?.length) return;
    startUpload(file_upload.files[0]);
  };

  const startUpload = (file: File) => {
    uploadProgress = 0.001;
    uploading = true;
    filename = file.name;
    getSignedRequest(file);
  };

  const handleDrop = (e: DragEvent) => {
    e.preventDefault();
    dragOver = false;
    if (e.dataTransfer?.files?.length) {
      startUpload(e.dataTransfer.files[0]);
    }
  };

  const handleDragOver = (e: DragEvent) => {
    e.preventDefault();
    dragOver = true;
  };

  const handleDragLeave = () => {
    dragOver = false;
  };

  const uploadFile = (file: File, s3Data: any) => {
    const postData = new FormData();
    for (let key in s3Data.data.fields) {
      postData.append(key, s3Data.data.fields[key]);
    }
    postData.append("file", file);

    const xhr = new XMLHttpRequest();
    xhr.open("POST", s3Data.url);

    xhr.onreadystatechange = () => {
      if (xhr.readyState === 4) {
        if (xhr.status === 200 || xhr.status === 204) {
          pullToServer(s3Data.url);
        } else {
          alert("File could not be uploaded!");
          uploading = false;
        }
      }
    };

    xhr.upload.addEventListener("progress", (e) => {
      uploadProgress = e.loaded / e.total;
    });

    xhr.send(postData);
  };

  const pullToServer = async (dlurl: string) => {
    serverPulling = true;

    const response = await fetch(`${url}/rpc/create_livewire_pull_file_from_space`, {
      method: "post",
      body: JSON.stringify({
        url: dlurl,
        wireident: yyyy.createNewLiveWire.data.wireident,
      }),
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + yyyy.sesh.logtoken.token,
      },
    });

    if (response.ok) {
      let data = await response.json();
      data = JSON.parse(data);
      yyyy.createNewLiveWire.data.ext_schema = data.ext_schema;
      yyyy.createNewLiveWire.layers = data.lyrs;
      // Move to configure step
      yyyy.createNewLiveWire.name = false;
      yyyy.createNewLiveWire.upload = false;
    } else {
      const data = await response.json();
      console.error(data);
      alert("Failed to process file on server");
    }

    serverPulling = false;
    uploading = false;
  };

  const cancel = async () => {
    if (yyyy.createNewLiveWire.data?.wireident) {
      await fetch(`${url}/livewires?wireident=eq.${yyyy.createNewLiveWire.data.wireident}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + yyyy.sesh.logtoken.token,
        },
      });
    }
    delete yyyy.createNewLiveWire;
    goto("/livewires");
  };
</script>

<div class="nameupload-container">
  <div class="nameupload-card">
    <header class="nameupload-header">
      <h1 class="title">Create New Livewire</h1>
      <p class="subtitle">
        {#if step === "details"}
          Configure your livewire settings
        {:else}
          Upload your data
        {/if}
      </p>
    </header>

    <div class="nameupload-body">
      {#if step === "details"}
        <!-- Details Form -->
        <div class="form-section">
          <div class="form-row three-col">
            <div class="field">
              <label class="label" for="lw-name">Name</label>
              <div class="control" class:is-loading={lw_name.loading}>
                <input
                  id="lw-name"
                  class="input"
                  class:is-success={lw_name.success}
                  class:is-danger={lw_name.danger}
                  type="text"
                  placeholder="Give your LiveWire a name"
                  bind:value={lw_name.value}
                  onblur={nameCheck}
                />
              </div>
              {#if lw_name.danger}
                <p class="help is-danger">{lw_name.message}</p>
              {/if}
            </div>

            <div class="field">
              <label class="label" for="lw-srid">SRID</label>
              <div class="control" class:is-loading={lw_srid.loading}>
                <input
                  id="lw-srid"
                  class="input"
                  class:is-success={lw_srid.success}
                  class:is-danger={lw_srid.danger}
                  type="text"
                  placeholder="EPSG code (e.g. 4326)"
                  bind:value={lw_srid.value}
                  onblur={sridCheck}
                />
              </div>
              {#if lw_srid.danger}
                <p class="help is-danger">{lw_srid.message}</p>
              {/if}
            </div>

            <div class="field">
              <label class="label" for="lw-tolr">Tolerance</label>
              <div class="control" class:is-loading={lw_tolr.loading}>
                <input
                  id="lw-tolr"
                  class="input"
                  class:is-success={lw_tolr.success}
                  class:is-danger={lw_tolr.danger}
                  class:is-warning={lw_tolr.warning}
                  type="text"
                  placeholder="Snapping tolerance"
                  bind:value={lw_tolr.value}
                  onkeyup={tolrCheck}
                />
              </div>
              {#if lw_tolr.danger || lw_tolr.warning}
                <p class="help" class:is-danger={lw_tolr.danger} class:is-warning={lw_tolr.warning}>
                  {lw_tolr.message}
                </p>
              {/if}
            </div>
          </div>

          <div class="field">
            <label class="label" for="lw-desc">Description</label>
            <div class="control">
              <textarea
                id="lw-desc"
                class="textarea"
                rows="4"
                placeholder="Describe this livewire (optional - we'll generate one if left blank)"
                bind:value={lw_desc.value}
              ></textarea>
            </div>
          </div>
        </div>

      {:else}
        <!-- Upload Section -->
        <div class="upload-section">
          {#if uploadProgress === 0}
            <div class="upload-prompt">
              <p>Upload your data for <strong>{yyyy.createNewLiveWire.livewire}</strong></p>
              <p class="upload-hint">Supported formats: GeoPackage, ESRI File Geodatabase, or zipped Shapefile</p>

              <button
                type="button"
                class="upload-btn"
                class:drag-over={dragOver}
                onclick={() => file_upload.click()}
                ondrop={handleDrop}
                ondragover={handleDragOver}
                ondragleave={handleDragLeave}
              >
                <Fa icon={faUpload} size="2x" />
                <span>Drag & drop your file here</span>
                <span class="upload-or">or click to browse</span>
              </button>
              <input
                bind:this={file_upload}
                type="file"
                class="hidden-input"
                onchange={fileKickOff}
              />
            </div>
          {:else}
            <div class="upload-progress">
              <h2>{Math.round(uploadProgress * 100)}%</h2>
              <progress class="progress is-primary" value={uploadProgress} max="1"></progress>
              <p class="filename">{filename}</p>
              {#if serverPulling}
                <p class="processing">Processing file on server...</p>
              {/if}
            </div>
          {/if}
        </div>
      {/if}
    </div>

    <footer class="nameupload-footer">
      <button class="button is-danger is-outlined" onclick={cancel} disabled={uploading || serverPulling}>
        Cancel
      </button>

      {#if step === "details"}
        <button
          class="button is-primary"
          class:is-loading={creatingLivewire}
          disabled={!canProceed}
          onclick={createAndShowUpload}
        >
          <span class="icon"><Fa icon={faCheck} /></span>
          <span>Next</span>
        </button>
      {/if}
    </footer>
  </div>
</div>

<style>
  .nameupload-container {
    position: fixed;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    margin: auto;
    width: 70%;
    max-width: 900px;
    height: fit-content;
    max-height: 90vh;
    padding: 1rem;
    z-index: 5;
  }

  .nameupload-card {
    background: rgba(255, 255, 255, 0.95);
    border-radius: 8px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
    overflow: hidden;
  }

  .nameupload-header {
    background: linear-gradient(135deg, #ff3e00 0%, #ff6b35 100%);
    color: white;
    padding: 1.5rem 2rem;
  }

  .nameupload-header .title {
    color: white;
    font-size: 1.5rem;
    font-weight: 300;
    text-transform: uppercase;
    margin-bottom: 0.25rem;
  }

  .nameupload-header .subtitle {
    color: rgba(255, 255, 255, 0.85);
    font-size: 0.9rem;
  }

  .nameupload-body {
    padding: 2rem;
    min-height: 300px;
  }

  .nameupload-footer {
    display: flex;
    justify-content: space-between;
    padding: 1rem 2rem;
    border-top: 1px solid #e0e0e0;
    background: #fafafa;
  }

  /* Form styles */
  .form-section {
    max-width: 800px;
    margin: 0 auto;
  }

  .form-row {
    display: grid;
    gap: 1.5rem;
    margin-bottom: 1.5rem;
  }

  .form-row.three-col {
    grid-template-columns: 2fr 1fr 1fr;
  }

  .field {
    margin-bottom: 0;
  }

  .label {
    font-weight: 600;
    font-size: 0.9rem;
    margin-bottom: 0.5rem;
  }

  .control.is-loading::after {
    position: absolute;
    right: 0.75rem;
    top: 0.75rem;
  }

  .help {
    font-size: 0.8rem;
    margin-top: 0.25rem;
  }

  /* Upload styles */
  .upload-section {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 250px;
    width: 100%;
  }

  .upload-prompt {
    text-align: center;
    width: 100%;
  }

  .upload-prompt p {
    margin-bottom: 0.5rem;
  }

  .upload-hint {
    color: #666;
    font-size: 0.9rem;
    margin-bottom: 1.5rem !important;
  }

  .upload-btn {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.75rem;
    padding: 3rem;
    width: 100%;
    background: #f5f5f5;
    border: 2px dashed #ccc;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s;
    color: #666;
  }

  .upload-btn:hover,
  .upload-btn.drag-over {
    background: #fff3e0;
    border-color: #ff3e00;
    color: #ff3e00;
  }

  .upload-or {
    font-size: 0.85rem;
    opacity: 0.7;
  }

  .hidden-input {
    display: none;
  }

  .upload-progress {
    text-align: center;
    width: 100%;
    max-width: 400px;
  }

  .upload-progress h2 {
    font-size: 3rem;
    font-weight: 200;
    color: #ff3e00;
    margin-bottom: 1rem;
  }

  .upload-progress .progress {
    height: 8px;
    margin-bottom: 1rem;
  }

  .filename {
    color: #666;
    font-size: 0.9rem;
  }

  .processing {
    color: #ff3e00;
    font-size: 0.9rem;
    margin-top: 1rem;
    animation: pulse 1.5s infinite;
  }

  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
  }
</style>
